import { ChangeEvent, CrudDecoratorCloseEvent, DataDisplayEvent, DataHeader, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DropdownItemResolver, DropdownSelectionEvent, SelectionEvent, Table, TableRow, Textbox } from "@mcleod/components";
import { Api, Model, ModelRow, ObjectUtil } from "@mcleod/core";
import { CommonDialogs } from "../CommonDialogs";
import { AutogenLayoutUserGroup } from "./autogen/AutogenLayoutUserGroup";

enum UserGroupType {
    PERMISSION = "P",
    SAVED_SEARCH = "S",
    BOTH = "B"
}

type MembershipChanges = {
    add_user_ids: string[];
    delete_user_ids: string[];
};

export class UserGroup extends AutogenLayoutUserGroup {
    private originalUsersInGroup: Set<string>;

    async onLoad() {
        this.tableUsersInGroup.addFilterChangedListener(() => this.toggleAllButtons());
        this.tableUsersNotInGroup.addFilterChangedListener(() => this.toggleAllButtons());
    }

    syncTableUsersNotInGroupFilter() {
        this.tableUsersNotInGroup.customFilter = (row: TableRow) => {
            const isActiveUser = row.data.getBoolean("is_active", true);
            const showAllUsers = this.checkboxAllUsers.checked;
            const notInOtherGroup = !row.data.getBoolean("in_other_group", false);
            return isActiveUser && (showAllUsers || notInOtherGroup);
        }
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.promptToSaveChanges();
        dataHeader.showDelete = true;
        // Add a beforeClose listener to check edits
        dataHeader.addBeforeCloseListener(async (event: CrudDecoratorCloseEvent) => {
            if (event.isCancel() && this.hasMembershipChanges()) {
                event.preventDefault();
                    const message = "Unsaved changes are present.  Do you wish to leave without saving changes?";
                    const dialogProps = {
                        yesButtonCaption: "Leave",
                        noButtonCaption: "Stay"
                    };
                    const leave = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
                    if (leave) {
                        this.slideOut();
                    }
            }
        });
    }

    private getUserIdsFromDataSource(dataSource: DataSource): Set<string> {
        return new Set(dataSource.data.map(row => row.get("id")));
    }

    private async setupUserTables() {
        try {
            this.setTransferButtonsEnabled(false);
            this.sourceUsersInGroup.clear();
            this.sourceUsersNotInGroup.clear();
            this.setTablesBusy(true);
            const userData = await this.getUserData();

            for (const row of userData) {
                this.setUserLookupModelData(row);
                if (row.getBoolean("in_requested_group", false)) {
                    this.sourceUsersInGroup.data.push(row);
                } else {
                    this.sourceUsersNotInGroup.data.push(row);
                }
            }

            this.sourceUsersInGroup.displayDataInBoundComponents();
            this.sourceUsersNotInGroup.displayDataInBoundComponents();
            this.originalUsersInGroup = this.getUserIdsFromDataSource(this.sourceUsersInGroup);
        } finally {
            this.toggleAllButtons()
            this.setTablesBusy(false);
        }
    }

    private setUserLookupModelData(row: ModelRow) {
        const userId = row.get("id");
        const userName = row.get("name") || userId;
        row.setLookupModelData("id", { id: userId, name: userName });
    }

    private async getUserData(): Promise<ModelRow[]> {
        this.syncTableUsersNotInGroupFilter();

        const userData = await Model.search("common/user-group-users", {
            user_type: this.activeRow.get("user_type"),
            group_type: this.activeRow.get("user_group_type"),
            user_group_id: this.activeRow.get("id"),
            include_users_in_other_groups: this.checkboxAllUsers.checked
        });
        return userData.modelRows;
    }

    tableUsersNotInGroupOnSelect(event: SelectionEvent) {
        this.buttonAddSelected.enabled = this.tableUsersNotInGroup.selectedRows.length > 0;
    }

    tableUsersInGroupOnSelect(event: SelectionEvent) {
        this.buttonRemoveSelected.enabled = this.tableUsersInGroup.selectedRows.length > 0;
    }

    private setTablesBusy(value: boolean) {
        this.tableUsersInGroup.busy = value;
        this.tableUsersNotInGroup.busy = value;
    }

    private setTransferButtonsEnabled(enabled: boolean): void {
        this.buttonRemoveSelected.enabled = enabled;
        this.buttonAddSelected.enabled = enabled;
        this.buttonRemoveAll.enabled = enabled;
        this.buttonAddAll.enabled = enabled;
    }

    private toggleAllButtons() {
        this.buttonRemoveAll.enabled = this.tableUsersInGroup.rows.length > 0;
        this.buttonAddAll.enabled = this.tableUsersNotInGroup.rows.length > 0;
    }

    showConfirmationDialog(isSave: boolean): Promise<boolean> {
        return CommonDialogs.showYesNo(
            `Are you sure you would like to ${isSave ? "save" : "revert"} your changes?`,
            `${isSave ? "Save" : "Revert"} Changes`
        );
    }

    async postGroupMembership() {
        const changes = this.getMembershipChanges();
        if (ObjectUtil.isEmptyObject(changes, true))
            return;

        Api.update("common/user/update-group-membership", {
             "user_group_id": this.activeRow.get("id"),
            ...ObjectUtil.deleteNullOrEmptyProps(changes)
        }).then(_ => {
            this.setupUserTables()
        })
    }

    buttonRemoveSelectedOnClick() {
        this.transferRowsOnClick(this.tableUsersInGroup, this.tableUsersInGroup.selectedRows, this.tableUsersNotInGroup);
    }

    buttonAddSelectedOnClick() {
        this.transferRowsOnClick(this.tableUsersNotInGroup, this.tableUsersNotInGroup.selectedRows, this.tableUsersInGroup);
    }

    buttonRemoveAllOnClick() {
        this.transferRowsOnClick(this.tableUsersInGroup, this.tableUsersInGroup.filteredRows, this.tableUsersNotInGroup);
    }

    buttonAddAllOnClick() {
        this.transferRowsOnClick(this.tableUsersNotInGroup, this.tableUsersNotInGroup.filteredRows, this.tableUsersInGroup);
    }

    private transferRowsOnClick(fromTable: Table, rowsToTransfer: TableRow[], toTable: Table) {
        toTable.selectedRows = [];
        const rows = [...rowsToTransfer];

        for (let i = rows.length - 1; i >= 0; i--) {
            this.transferUser(rows[i].data, fromTable.dataSource, toTable.dataSource);
        }

        toTable.clearSort();
        toTable.applySort("name");
    }

    private transferUser(userRow: ModelRow, sourceFrom: DataSource, sourceTo: DataSource): void {
        if (userRow == null || this.activeRow == null)
            return;
        sourceTo.addRow(userRow);
        const index = sourceFrom.data.indexOf(userRow);
        if (index >= 0)
            sourceFrom._deleteTableRow(userRow, false);
        this.toggleAllButtons();
        this.mainDataSource.notifyHasChangedComponents(this.hasMembershipChanges());
    }

    private hasMembershipChanges(): boolean {
        return !ObjectUtil.isEmptyObject(this.getMembershipChanges(), true);
    }

    private getMembershipChanges(): MembershipChanges {
        const currentUsersInGroup = this.getUserIdsFromDataSource(this.sourceUsersInGroup);
        const add_user_ids: string[] = [];
        const delete_user_ids: string[] = [];

        for (const userId of currentUsersInGroup) {
            if (!this.originalUsersInGroup.has(userId)) {
                add_user_ids.push(userId);
            }
        }

        for (const userId of this.originalUsersInGroup) {
            if (!currentUsersInGroup.has(userId)) {
                delete_user_ids.push(userId);
            }
        }

        return { add_user_ids, delete_user_ids };
    }

    /** This is an event handler for the onDataDisplay event of textboxUserInGroup. */
    textboxUserOnDataDisplay(event: DataDisplayEvent) {
        const textbox = event.target as Textbox;
        if (!textbox.boundRow?.getBoolean("is_active", true)) {
            textbox.setProps({
                color: "default.lightest",
                tooltipDynamicContent: {
                    text: "This user is inactive",
                    color: "error",
                    fontBold: true,
                    fillRow: true
                }
            });
        }
    }

    /** This is an event handler for the afterExecution event of sourceUserGroup. */
    sourceUserGroupAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.setupUserTables();
        } else if (event.getAction() === DataSourceAction.UPDATE || event.getAction() === DataSourceAction.ADD) {
            this.postGroupMembership();
        }
    }

    /** This is an event handler for the afterModeChange event of sourceUserGroup. */
    sourceUserGroupAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode === DataSourceMode.ADD) {
            this.setupUserTables();
            this.tableUsersNotInGroup.addDataDisplayListener(() => this.toggleAllButtons());
        } else if (event.newMode === DataSourceMode.UPDATE) {
            this.setUserGroupTypeItems();
        }
    }

    private setUserGroupTypeItems() {
        const userGroupType = this.activeRow?.get("user_group_type");
        const allItems = DropdownItemResolver.createDropdownItems(this.textboxUserGroupType.items);
        if (userGroupType === UserGroupType.PERMISSION) {
            this.textboxUserGroupType.items = allItems.filter(item => item.value !== UserGroupType.SAVED_SEARCH);
        } else if (userGroupType === UserGroupType.SAVED_SEARCH) {
            this.textboxUserGroupType.items = allItems.filter(item => item.value !== UserGroupType.PERMISSION);
        } else if (userGroupType === UserGroupType.BOTH) {
            this.textboxUserGroupType.items = allItems.filter(item => item.value === UserGroupType.BOTH);
        }
    }

    sourceUsersInGroupAfterModeChange(event: DataSourceModeChangeEvent) {
        event.stopPropagation();
    }

    textboxUserTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.userInitiatedChange) {
            this.setupUserTables();
        }
    }

    textboxUserGroupTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
        this.searchUsersNotInGroupIfNeeded(event, !this.checkboxAllUsers.checked);
    }

    checkboxAllUsersOnChange(event: ChangeEvent) {
        this.searchUsersNotInGroupIfNeeded(event);
    }

    private async searchUsersNotInGroupIfNeeded(event: ChangeEvent | DropdownSelectionEvent, shouldSearch: boolean = true) {
        if (!event.userInitiatedChange || !shouldSearch) {
            return;
        }

        try {
            this.setTransferButtonsEnabled(false);
            this.sourceUsersNotInGroup.clear();
            this.tableUsersNotInGroup.busy = true;

            const userData = await this.getUserData();
            const usersInGroupIds = this.getUserIdsFromDataSource(this.sourceUsersInGroup);
            for (const row of userData) {
                if (!usersInGroupIds.has(row.get("id"))) {
                    this.setUserLookupModelData(row);
                    this.sourceUsersNotInGroup.data.push(row);
                }
            }
            this.sourceUsersNotInGroup.displayDataInBoundComponents();
        } finally {
            this.toggleAllButtons();
             this.tableUsersNotInGroup.busy = false;
        }
    }
}
